// Editor de enquadramento da foto do produto — Onda Avaliacao de Foto.
// Mesmo gesto da foto de perfil do WhatsApp: arrasta pra posicionar e usa o
// zoom pra aproximar (produto pequeno no quadro) ou afastar. Tambem gira a foto
// no proprio eixo (90 em 90 ou ajuste fino). Recorta NA RESOLUCAO DA ORIGEM e no
// formato do produto — enquadrar nao pode encolher, achatar nem decepar a foto
// que esta subindo (ver o comentario longo em `recortarArea`). Usa react-easy-crop.
import { useCallback, useEffect, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';
import { RotateCcw, RotateCw } from 'lucide-react';
// A geometria mora numa peca propria e testada (`enquadrar-foto-conta.ts`) — esta
// conta ja regrediu duas vezes escrita solta aqui dentro, com todos os portoes
// verdes. Mexeu na moldura ou na regua? O teste de la e quem responde.
import {
  caixaGirada,
  masterQueOServidorVaiVer,
  proporcaoDaMoldura,
  recorteUtil,
  saidaDoEditor,
  LADO_MINIMO_MASTER,
  type Medida,
  type Retangulo,
} from './enquadrar-foto-conta';

interface Props {
  fotoUrl: string;
  onAplicar: (blob: Blob, semAlteracao: boolean) => void;
  onCancelar: () => void;
  aplicando?: boolean;
}

function carregarImagem(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('falha ao carregar imagem'));
    img.src = url;
  });
}

// Mantem o angulo na faixa -180..180 (os botoes de 90 podem estourar isso).
function normalizarAngulo(graus: number): number {
  let n = graus % 360;
  if (n > 180) n -= 360;
  if (n < -180) n += 360;
  return n;
}

// A imagem tem transparencia de verdade? Amostra reduzida — so precisamos saber
// se ha canal alfa em uso, nao onde. Decide o fundo do recorte e o formato de
// saida: foto ja recortada pelo Ruan NAO pode ser achatada em branco aqui.
function temTransparencia(img: HTMLImageElement): boolean {
  const lado = 100;
  const c = document.createElement('canvas');
  c.width = lado;
  c.height = lado;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  if (!ctx) return false;
  ctx.drawImage(img, 0, 0, lado, lado);
  try {
    const { data } = ctx.getImageData(0, 0, lado, lado);
    for (let i = 3; i < data.length; i += 4) if ((data[i] ?? 255) < 250) return true;
  } catch {
    /* canvas contaminado — trata como opaca */
  }
  return false;
}

// Lado do canvas de MEDICAO. A conta roda a cada mexida no enquadramento, entao
// ela nao pode rodar na resolucao da foto; 400px custa ~2ms e erra poucos pixels
// — coberto pela folga do `saidaDoEditor`.
const LADO_MEDICAO = 400;

/**
 * Mede o PRODUTO — a caixa dos pixels que nao sao transparentes — do jeito que o
 * SERVIDOR mede, e devolve em pixels da origem.
 *
 * ⚠️ Por que nao basta a geometria: o `gerarMasterWebp` roda `-trim`, que acha a
 * caixa do PRODUTO, e produto afilado GIRADO nao encosta nos cantos do proprio
 * retangulo. No terminal do Ruan a caixa da imagem a 44 graus da 1766x1803 e a do
 * produto da 1597x1670 — 8% de diferenca. Compensar pela geometria deixava o
 * master 13px abaixo da regua e o upload voltava 422 (medido na VPS, com o
 * `-trim` de verdade, antes desta peca existir).
 *
 * Devolve `null` quando nao da pra medir (canvas contaminado, sem contexto). Ai a
 * peca cai no comportamento antigo e NAO compensa nada — falha fechada.
 */
function medirProduto(
  img: HTMLImageElement,
  rotacaoGraus: number,
  recorte: { x: number; y: number; largura: number; altura: number },
): { girado: Medida; noRecorte: Medida } | null {
  const { largura: bw, altura: bh } = caixaGirada(img.width, img.height, rotacaoGraus);
  const k = Math.min(1, LADO_MEDICAO / Math.max(bw, bh));
  const cw = Math.max(1, Math.ceil(bw * k));
  const ch = Math.max(1, Math.ceil(bh * k));
  const c = document.createElement('canvas');
  c.width = cw;
  c.height = ch;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.scale(k, k);
  ctx.translate(bw / 2, bh / 2);
  ctx.rotate((rotacaoGraus * Math.PI) / 180);
  ctx.drawImage(img, -img.width / 2, -img.height / 2);

  let dados: Uint8ClampedArray;
  try {
    dados = ctx.getImageData(0, 0, cw, ch).data;
  } catch {
    return null; // canvas contaminado — sem medicao, sem compensacao
  }

  // A janela do recorte em pixels DESTE canvas.
  const rx0 = recorte.x * k;
  const ry0 = recorte.y * k;
  const rx1 = (recorte.x + recorte.largura) * k;
  const ry1 = (recorte.y + recorte.altura) * k;

  let gx0 = Infinity, gy0 = Infinity, gx1 = -Infinity, gy1 = -Infinity;
  let rx0f = Infinity, ry0f = Infinity, rx1f = -Infinity, ry1f = -Infinity;
  for (let y = 0; y < ch; y++) {
    for (let x = 0; x < cw; x++) {
      if ((dados[(y * cw + x) * 4 + 3] ?? 0) === 0) continue;
      if (x < gx0) gx0 = x;
      if (x > gx1) gx1 = x;
      if (y < gy0) gy0 = y;
      if (y > gy1) gy1 = y;
      if (x + 1 > rx0 && x < rx1 && y + 1 > ry0 && y < ry1) {
        if (x < rx0f) rx0f = x;
        if (x > rx1f) rx1f = x;
        if (y < ry0f) ry0f = y;
        if (y > ry1f) ry1f = y;
      }
    }
  }
  if (gx1 < gx0 || gy1 < gy0) return null; // imagem inteiramente transparente

  const caixaDe = (x0: number, y0: number, x1: number, y1: number): Medida =>
    x1 < x0 || y1 < y0
      ? { largura: 0, altura: 0 }
      : { largura: (x1 - x0 + 1) / k, altura: (y1 - y0 + 1) / k };

  return { girado: caixaDe(gx0, gy0, gx1, gy1), noRecorte: caixaDe(rx0f, ry0f, rx1f, ry1f) };
}

// Recorta a area escolhida, aplicando a rotacao. A saida tem a LARGURA e a
// ALTURA da area — nao e mais um quadrado.
//
// ⚠️ O tamanho sai da propria area recortada, em pixels da imagem de ORIGEM —
// nunca um numero fixo. Ate 2026-08-08 esta funcao redesenhava tudo num quadro
// cravado de 1200x1200 e pintava o fundo de branco antes: o Ruan preparava o
// master fora (2000px+, fundo ja removido), o navegador o reduzia pra 1200 e
// achatava a transparencia, e o servidor recebia uma foto que NAO tinha como
// passar na regua de 2000px — recortando de novo, com o rembg, um fundo que ja
// estava recortado. Nenhuma foto que entrou pela ficha do produto pode virar
// master enquanto isso existiu, e o original em alta nunca chegou a sair da
// maquina dele: perda irreversivel, e calada.
//
// ⚠️ E aquele conserto deixou METADE do problema de pe (DA-525, 13/08/2026): a
// resolucao passou a ser preservada, mas a saida continuou QUADRADA. Num perfil
// de aluminio de 2000x796 o maior quadrado possivel e limitado pela ALTURA —
// 796x796 —, entao o navegador decepava o produto e mandava 796px pro servidor,
// que recusava por resolucao. O servidor NUNCA exigiu quadrado: a regua do
// master mede o maior lado e o campo `quadrado` do analisarMaster e informativo,
// declaradamente pra caber cabo e perfil. Era a TELA mais rigida que o sistema.
//
// O editor existe pra ENQUADRAR (produto que estoura a moldura, foto torta) —
// enquadrar nao e reamostrar nem decepar.
//
// ⚠️ O recorte e o tamanho de saida chegam PRONTOS, calculados pela peca junto
// com o numero que a tarja mostrou. Recalcular aqui foi o que ja deixou a tela
// prometendo um tamanho e o arquivo saindo com outro.
async function recortarArea(
  src: string,
  recorte: Retangulo,
  saida: Medida,
  rotacaoGraus: number,
): Promise<Blob> {
  const img = await carregarImagem(src);
  const rad = (rotacaoGraus * Math.PI) / 180;
  const transparente = temTransparencia(img);

  // Canvas intermediario: a imagem ja rotacionada, dentro da sua bounding box.
  // area (croppedAreaPixels do react-easy-crop) e relativo a essa bounding box.
  const { largura: bw, altura: bh } = caixaGirada(img.width, img.height, rotacaoGraus);
  const rot = document.createElement('canvas');
  rot.width = bw;
  rot.height = bh;
  const rctx = rot.getContext('2d');
  if (!rctx) throw new Error('canvas indisponivel');
  // So pinta branco quando a origem e opaca. Origem transparente fica transparente:
  // o canto vazio deixado pela rotacao vira buraco, nao mancha branca.
  if (!transparente) {
    rctx.fillStyle = '#ffffff';
    rctx.fillRect(0, 0, bw, bh);
  }
  rctx.translate(bw / 2, bh / 2);
  rctx.rotate(rad);
  rctx.drawImage(img, -img.width / 2, -img.height / 2);

  // Canvas final. Dois pontos que a peca resolve: (1) desenha so o pedaco que tem
  // foto, sem a margem vazia que o `-trim` do servidor apararia de qualquer jeito;
  // (2) sai no tamanho que devolve o que o GIRO tirou do produto.
  const larguraSaida = Math.max(1, saida.largura);
  const alturaSaida = Math.max(1, saida.altura);
  const canvas = document.createElement('canvas');
  canvas.width = larguraSaida;
  canvas.height = alturaSaida;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas indisponivel');
  if (!transparente) {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, larguraSaida, alturaSaida);
  }
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(rot, recorte.x, recorte.y, recorte.largura, recorte.altura, 0, 0, larguraSaida, alturaSaida);
  // SEMPRE WebP — inclusive com transparencia. O WebP carrega canal alfa (medido
  // no Chrome: canto vazio volta com alpha=0 e o produto com 255) e pesa uma
  // fracao do PNG na resolucao que o master exige. Foi o PNG que obrigaria a
  // subir o teto de upload de 5 MB, e o teto fica onde estava.
  //
  // ⚠️ Desde 04/11/2026 e' PNG: a foto ajustada passou a SUBSTITUIR o original no acervo, e o Ruan
  // pediu "desde que nao perca qualidade". Medido no Chrome com o master 2503x2372 da chave 1765:
  // WebP 0,95 = 212 KB com perda; WebP 1,0 = 826 KB e AINDA com perda (ate 63 tons em 70 mil pontos);
  // PNG = 1,1 MB (1,3 MB girado 17 graus), so 1 tom de diferenca na borda semitransparente, efeito do
  // proprio canvas. Cabe nos 5 MB com folga.
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('falha ao gerar imagem'))), 'image/png');
  });
}

export function EditorEnquadrarFoto({ fotoUrl, onAplicar, onCancelar, aplicando }: Props) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotacao, setRotacao] = useState(0);
  const [area, setArea] = useState<Area | null>(null);
  const [processando, setProcessando] = useState(false);
  // O quadro de recorte segue a PROPORCAO DA FOTO, nao um quadrado (DA-525): num
  // produto comprido o quadrado nao e enquadramento, e guilhotina. Comeca em null
  // e o Cropper so entra quando a medida chega — `aspect` chutado em 1 enquanto a
  // imagem carrega faria o quadro pular de forma na frente do operador.
  //
  // ⚠️ E sem `cropSize`. Ele existia pra alargar a moldura quadrada, que encolhia
  // num produto fino, e cravava a moldura em 92% do preview — enquanto a imagem,
  // no zoom 1, ocupa 100% dele. Resultado: TODA foto perdia 8% nas bordas, calada.
  // Medido na verificacao desta propria onda: um 2000x796 saiu 1838x730 e foi
  // recusado por resolucao. Com a proporcao certa a moldura ja nasce do tamanho
  // da foto, entao o remendo deixou de ter motivo. Zoom 1 = foto inteira, intacta.
  const [proporcaoCrua, setProporcaoCrua] = useState<number | null>(null);
  const [medidaOriginal, setMedidaOriginal] = useState<Medida | null>(null);
  // A imagem carregada fica guardada: a medicao do produto roda em cima dela a
  // cada mexida, e recarregar por `src` a cada quadro seria absurdo.
  const [imagem, setImagem] = useState<HTMLImageElement | null>(null);
  // O produto PARADO — o que a foto vale sem giro nenhum. E' o portao que autoriza
  // (ou nao) compensar a inclinacao depois. `null` = foto opaca ou canvas
  // contaminado: sem medicao nao ha compensacao.
  const [produtoParado, setProdutoParado] = useState<Medida | null>(null);
  const [medicao, setMedicao] = useState<{ girado: Medida; noRecorte: Medida } | null>(null);

  useEffect(() => {
    let vivo = true;
    carregarImagem(fotoUrl)
      .then((img) => {
        if (!vivo || img.naturalWidth <= 0 || img.naturalHeight <= 0) return;
        setProporcaoCrua(img.naturalWidth / img.naturalHeight);
        setMedidaOriginal({ largura: img.naturalWidth, altura: img.naturalHeight });
        setImagem(img);
        // ⚠️ So mede o produto quando a foto TEM transparencia. Numa foto opaca
        // quem separa o produto do fundo e o rembg, la no servidor, e nao ha como
        // prever daqui o que ele vai devolver — entao nao se promete nada.
        const parado = temTransparencia(img)
          ? medirProduto(img, 0, { x: 0, y: 0, largura: img.width, altura: img.height })
          : null;
        setProdutoParado(parado?.girado ?? null);
      })
      .catch(() => { if (vivo) setProporcaoCrua(1); });
    return () => { vivo = false; };
  }, [fotoUrl]);

  // A cada mexida no enquadramento, remede o produto no angulo e no recorte atuais
  // — e' o unico jeito de a tarja dizer o que o servidor vai medir.
  useEffect(() => {
    if (!imagem || !area || !produtoParado) { setMedicao(null); return; }
    const caixa = caixaGirada(imagem.width, imagem.height, rotacao);
    const dentro = recorteUtil({ x: area.x, y: area.y, largura: area.width, altura: area.height }, caixa);
    setMedicao(medirProduto(imagem, rotacao, dentro));
  }, [imagem, area, rotacao, produtoParado]);

  // ⚠️ A moldura segue a proporcao da foto **JA GIRADA**, nao a da foto crua — e e
  // ela que faz o quadro CRESCER pra comportar a imagem inclinada. Sem isso o
  // react-easy-crop encaixa uma moldura de proporcao FIXA dentro da caixa da foto
  // girada, e o quadro ENCOLHE: num cadeado em pe de 953x3066, girar 90 graus no
  // zoom 1 saia `296x953` e o botao recusava — virar uma foto de lado era
  // impossivel. Com a proporcao girada o mesmo gesto sai `3066x953`, a foto
  // inteira. Girar deixa de custar resolucao — os numeros estao fixados em
  // `enquadrar-foto-conta.test.ts`, que o portao do deploy roda.
  //
  // Isto NAO e o `cropSize` de volta: quem calcula a moldura continua sendo o
  // `aspect`, como o README da biblioteca manda — so que agora informado do giro.
  // No giro 0 a conta e identica a de antes, entao o 2000x796 da DA-525 segue
  // saindo 2000x796 exato, sem os 8% que o `cropSize` comia.
  const proporcao = proporcaoCrua === null ? null : proporcaoDaMoldura(proporcaoCrua, rotacao);

  const onCropComplete = useCallback((_: Area, a: Area) => setArea(a), []);

  // O que vai sair daqui, em pixels — a mesma conta do recortarArea. Mostrado na
  // tela: recorte que nao alcanca a regua e recusado pelo servidor, e sem isso o
  // operador so descobre depois de enviar, sem saber pra que lado mexer o zoom.
  const resultado =
    area && medidaOriginal
      ? saidaDoEditor({
          area: { x: area.x, y: area.y, largura: area.width, altura: area.height },
          original: medidaOriginal,
          graus: rotacao,
          produtoNoRecorte: medicao?.noRecorte ?? null,
          produtoGirado: medicao?.girado ?? null,
          produtoParado,
        })
      : null;
  const saida = resultado?.saida ?? null;
  const ladoMaiorSaida = saida ? Math.max(saida.largura, saida.altura) : 0;
  const alcancaMaster = !!resultado && resultado.alcanca;
  // O giro cobrou caixa e a peca devolveu — vale dizer na tarja, senao o numero
  // exibido nao bate com o tamanho do recorte e parece erro de conta.
  const compensouGiro = (resultado?.escala ?? 1) > 1.001;
  // Sobram exatamente DOIS jeitos de nao alcancar a regua, e eles pedem conselhos
  // opostos: zoom pra dentro do produto (perda ESCOLHIDA, da' pra desfazer) ou foto
  // que ja nao valia master parada (nao ha angulo nem zoom que resolva — e' o
  // portao 1 da peca). Mandar "diminua o zoom" no segundo caso e o aviso que aponta
  // a alavanca errada da [DA-616] voltando pela outra porta.
  const passavaParado = !!produtoParado && masterQueOServidorVaiVer(produtoParado) >= LADO_MINIMO_MASTER;
  const maiorProdutoParado = produtoParado ? Math.round(Math.max(produtoParado.largura, produtoParado.altura)) : 0;
  const culpaDoZoom = zoom > 1.001 || !produtoParado;
  // Aplicou sem mexer em nada: sobe o ARQUIVO ORIGINAL em vez do redesenho do
  // canvas, que reencoda em WebP 0,95 e cobraria qualidade por uma etapa que nao
  // mudou nada. Tolerancia de 1px pro arredondamento do react-easy-crop.
  const semAlteracao =
    rotacao === 0 &&
    !!saida &&
    !!medidaOriginal &&
    Math.abs(saida.largura - medidaOriginal.largura) <= 1 &&
    Math.abs(saida.altura - medidaOriginal.altura) <= 1;

  async function aplicar() {
    if (!resultado) return;
    setProcessando(true);
    try {
      onAplicar(await recortarArea(fotoUrl, resultado.recorte, resultado.saida, rotacao), semAlteracao);
    } catch {
      setProcessando(false);
    }
  }

  const ocupado = aplicando || processando;

  return (
    <div className="flex flex-col items-center gap-3 text-sm">
      <p className="text-sm text-gray-600 text-center">
        Arraste a foto pra posicionar, use o zoom pra aproximar ou afastar e gire pra endireitar o produto.
      </p>

      <div className="relative w-full rounded-lg overflow-hidden" style={{ height: 460, background: '#1f2937' }}>
        {proporcao !== null && (
        <Cropper
          image={fotoUrl}
          crop={crop}
          zoom={zoom}
          rotation={rotacao}
          aspect={proporcao}
          minZoom={0.25}
          maxZoom={3}
          showGrid={false}
          restrictPosition={false}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onRotationChange={setRotacao}
          onCropComplete={onCropComplete}
        />
        )}
      </div>

      <div className="flex items-center gap-3 w-full" style={{ maxWidth: 420 }}>
        <span className="text-sm text-gray-500 w-12">Zoom</span>
        <input
          type="range"
          min={0.25}
          max={3}
          step={0.02}
          value={zoom}
          onChange={(e) => setZoom(Number(e.target.value))}
          className="flex-1"
        />
        {/* O passo de 0,02 a partir de 0,25 nunca cai em 1,00 exato — arrastando, o
            zoom pula de 0,99 pra 1,01. Numa foto que esta em cima da regua, esse
            unico passo e a diferenca entre salvar e nao salvar, e o caminho de volta
            era fechar e reabrir o editor. Mesmo idioma do "Zerar" do giro. */}
        <button
          type="button"
          onClick={() => setZoom(1)}
          disabled={zoom === 1}
          title="Voltar o zoom pro tamanho original"
          className="text-xs font-medium text-blue-600 hover:text-blue-800 disabled:text-gray-300 disabled:cursor-default flex-shrink-0"
        >
          1×
        </button>
      </div>

      <div className="flex items-center gap-2 w-full" style={{ maxWidth: 420 }}>
        <span className="text-sm text-gray-500 w-12">Girar</span>
        <button
          type="button"
          onClick={() => setRotacao((r) => normalizarAngulo(r - 90))}
          title="Girar 90 para a esquerda"
          className="flex items-center justify-center w-9 h-9 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 flex-shrink-0"
        >
          <RotateCcw size={16} />
        </button>
        <input
          type="range"
          min={-180}
          max={180}
          step={1}
          value={rotacao}
          onChange={(e) => setRotacao(Number(e.target.value))}
          className="flex-1"
        />
        <button
          type="button"
          onClick={() => setRotacao((r) => normalizarAngulo(r + 90))}
          title="Girar 90 para a direita"
          className="flex items-center justify-center w-9 h-9 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 flex-shrink-0"
        >
          <RotateCw size={16} />
        </button>
        <span className="text-xs text-gray-500 w-10 text-right tabular-nums">{Math.round(rotacao)}°</span>
        <button
          type="button"
          onClick={() => setRotacao(0)}
          disabled={rotacao === 0}
          title="Voltar a rotacao pro zero"
          className="text-xs font-medium text-blue-600 hover:text-blue-800 disabled:text-gray-300 disabled:cursor-default flex-shrink-0"
        >
          Zerar
        </button>
      </div>

      {/* O tamanho do que VAI SAIR, ao vivo. Sem isso o operador so descobre que
          o recorte nao alcanca a regua depois de enviar — e a mensagem do 422 nao
          diz pra que lado mexer o zoom. Botao apagado e mudo e beco sem saida. */}
      {/* Uma LINHA de proposito: o modal ja fecha justo na altura do tablet, e uma
          tarja de duas linhas empurrava o "Aplicar" pra fora do visivel
          (medido: 33px de sobra, botao 12px abaixo da borda). */}
      {saida && (
        <div
          className="w-full text-center text-xs rounded-lg px-3 py-1 whitespace-nowrap overflow-hidden"
          style={{
            maxWidth: 420,
            background: alcancaMaster ? '#fde68a' : '#f97316',
            color: '#000',
          }}
        >
          {alcancaMaster ? (
            <>
              Vai sair <strong>{saida.largura}×{saida.altura}px</strong>
              {semAlteracao
                ? ' — a foto inteira'
                : compensouGiro
                  ? ' — padrao Master ok (giro compensado)'
                  : ' — padrao Master ok'}
            </>
          ) : (
            <>
              <strong>{saida.largura}×{saida.altura}px</strong> — minimo {LADO_MINIMO_MASTER}px.{' '}
              {!culpaDoZoom && !passavaParado ? (
                <strong>O produto tem so {maiorProdutoParado}px</strong>
              ) : (
                <strong>Diminua o zoom</strong>
              )}
            </>
          )}
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onCancelar}
          disabled={ocupado}
          className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={aplicar}
          disabled={!area || ocupado || !alcancaMaster}
          title={!alcancaMaster ? `O recorte atual da ${ladoMaiorSaida}px no maior lado — diminua o zoom` : undefined}
          className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {/* So "Aplicar" (04/11/2026): a foto fica provisoria ate o Salvar do produto. */}
          {ocupado ? 'Aplicando...' : 'Aplicar'}
        </button>
      </div>
    </div>
  );
}
